import React, { useState, useEffect } from 'react';
import WebsiteForm from './WebsiteForm';
import WebsiteActions from './WebsiteActions';
import WebsiteStatusModal from './WebsiteStatusModal';
import DataTable from 'react-data-table-component';
import { toast } from 'react-toastify';
import WebsiteApiService from '../../ApiServices/WebsiteApiService/WebsiteApiService';
import { motion, AnimatePresence } from 'framer-motion';
import { FiSearch, FiPlus, FiX, FiCheck, FiClock, FiDownload } from 'react-icons/fi';
import { FaGlobe, FaBuilding, FaUserTie } from 'react-icons/fa';
import { DataStyles } from '../Common/DataTableStyles';
import PublisherApiService from '../../ApiServices/PublisherApiService/PublisherApiService';

const WebsiteTable = ({ user, isAdmin }) => {
  const [data, setData] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [search, setSearch] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [current, setCurrent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [publishers, setPublishers] = useState([]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = isAdmin
        ? await WebsiteApiService.GetAll()
        : await WebsiteApiService.GetAllWebByUserId(user.id);

      setData(res.data);
      setFiltered(res.data);
    } catch {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);


  const fetchPublishers = async () => {
    setLoading(true);
    try {
      const response = await PublisherApiService.getAllApproved();
      setPublishers(response.data);
    } catch (error) {
      toast.error("Failed to load publishers");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAdmin) fetchPublishers();
  }, [isAdmin]);

  const handleSearch = (e) => {
    const term = e.target.value.toLowerCase();
    setSearch(term);

    if (term === "") {
      setFiltered(data); // Reset to full data when search is cleared
    } else {
      const filteredResults = data.filter((item) =>
        item.name?.toLowerCase().includes(term) ||
        item.url?.toLowerCase().includes(term) ||
        item.companyName?.toLowerCase().includes(term) ||
        item.publisherName?.toLowerCase().includes(term) ||
        item.readableStatus?.toLowerCase().includes(term)
      );
      setFiltered(filteredResults);
    }
  };



  const StatusBadge = ({ status }) => {
    const statusConfig = {
      gettingready: { color: 'bg-blue-100 text-blue-800', icon: <FiClock className="mr-1" /> },
      approved: { color: 'bg-emerald-100 text-emerald-800', icon: <FiCheck className="mr-1" /> },
      disapproved: { color: 'bg-rose-100 text-rose-800', icon: <FiX className="mr-1" /> },
      default: { color: 'bg-gray-100 text-gray-800', icon: null }
    };

    const config = statusConfig[status?.toLowerCase()] || statusConfig.default;

    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${config.color}`}>
        {config.icon}
        {status}
      </span>
    );
  };

  const handleDelete = async (r) => {
    if (window.confirm("Are you sure you want to delete this website?")) {
      try {
        debugger
        await WebsiteApiService.Delete(r.websiteId);
        toast.success("Website deleted successfully");
        fetchData();
      } catch {
        toast.error("Failed to delete website");
      }
    }
  };

  const columns = [
    {
      name: "Name",
      selector: row => row.name,
      sortable: true,
      grow: 2,
      cell: row => (
        <div className="flex items-center">
          <FaGlobe className="text-indigo-500 mr-2" />
          <span className="font-medium text-gray-800">{row.name}</span>
        </div>
      )
    },
    {
      name: "URL",
      selector: row => row.url,
      sortable: true,
      grow: 2,
      cell: row => (
        <a
          href={row.Url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-indigo-600 hover:underline truncate block max-w-xs"
        >
          {row.url}
        </a>
      )
    },
    {
      name: "Company",
      selector: row => row.companyName,
      sortable: true,
      grow: 2,
      cell: row => (
        <div className="flex items-center">
          <FaBuilding className="text-gray-500 mr-2" />
          {row.companyName}
        </div>
      )
    },
    {
      name: "Publisher",
      selector: row => row.publisherName,
      sortable: true,
      grow: 2,
      omit: !isAdmin, // <- This hides the entire column if not admin
      cell: row => (
        <div className="flex items-center">
          <FaUserTie className="text-gray-500 mr-2" />
          {row.publisherName || "N/A"}
        </div>
      )
    },
    {
      name: "Status",
      selector: row => row.readableStatus,
      sortable: true,
      width: "180px",
      cell: row => <StatusBadge status={row.readableStatus} />

    },
    {
      name: "Actions",
      width: "300px",
      cell: row => (
        <WebsiteActions
          row={row}
          onEdit={() => { setCurrent(row); setModalOpen(true); }}
          onDelete={handleDelete}
          onDownload={() => WebsiteApiService.DownloadRejectionAttachment(row.WebsiteId)}
          isAdmin={isAdmin}
          onStatusModalOpen={() => { setCurrent(row); setStatusModalOpen(true); }}
          onSuccess={fetchData}
        />
      )
    }
  ];

  return (
    <div className="p-4 bg-white rounded-lg shadow">
      <div className="flex justify-between items-center mb-4 flex-wrap gap-3">
        <h2 className="text-xl font-semibold text-gray-800">All Websites</h2>
        <div className="flex items-center gap-3">
          <input
            type="text"
            placeholder="Search by name..."
            className="border border-gray-300 rounded-md px-3 py-1.5 w-64"
            value={search}
            onChange={handleSearch}
          />
          <button
            onClick={() => { setCurrent(null); setModalOpen(true); }}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            <FiPlus size={16} />
            Create Website
          </button>
        </div>
      </div>

      <DataTable
        columns={columns}
        data={filtered}
        progressPending={loading}
        pagination
        highlightOnHover
        striped
        noHeader
        responsive
        persistTableHead
        className="react-data-table"
      />

      <AnimatePresence>
        {modalOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="z-50"
            >
              <WebsiteForm
                item={current}
                onSubmit={async (formData) => {
                  try {
                    if (formData.websiteId) {
                      await WebsiteApiService.Update(formData);
                      toast.success("Website updated successfully");
                    } else {
                      await WebsiteApiService.Add(formData);
                      toast.success("Website created successfully");
                    }
                    setModalOpen(false);
                    fetchData();
                  } catch {
                    toast.error("Something went wrong");
                  }
                }}
                onCancel={() => setModalOpen(false)}
                publishers={publishers}
              />
            </motion.div>
          </div>
        )}
      </AnimatePresence>


    </div>
  );
};

export default WebsiteTable;